import { withAuth0 } from "@auth0/auth0-react";
import { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { httpClient } from "../../clients/auth/addTokenToHTTPRequests";
import { showModal, hideModal } from "../../redux/actions/modalActions";
import { checkUserStatus, getUserData } from "../../redux/actions/userActions";
import GrocerizeLoadingWheel from "../grocerize/GrocerizeLoadingWheel";
import SingleStepCheckout from "../checkout/SingleStepCheckout";
import "./styles/PremiumSignup.css";
import "./styles/FormTransition.css";
import { rejectCard } from "../../redux/actions/cardActions";
import { updateBank } from "../../redux/actions/bankActions";
import { TRIAL_PERIOD_DAYS } from "../../redux/enums";
import { updatePromoCode } from "../../redux/actions/promoCodeActions";

interface Props {
  showModal: any;
  hideModal: any;
  getUserData: any;
  auth0: any;
  user: UserState;
  onComplete: any;
  checkUserStatus: any;
  card?: CardState;
  rejectCard?: (isRejected: boolean) => void;
  bank?: BankState;
  updateBank?: (name: string | null) => void;
  promoCode?: PromoCodeState;
  updatePromoCode?: (payload: Partial<PromoCodeAction>) => void;
}

interface State {
  firstName: string;
  firstNameError: string;
  lastName: string;
  lastNameError: string;
  postcode: string;
  postcodeError: string;
  promoCode: string;
  promoCodeError: string;
  shoppingPreference: string;
  TandC: boolean;
  TandCError: string;
  mailing: boolean;
  attemptedToSubmit: boolean;
  showWelcome: boolean;
  isLoading: boolean;
  isSubmitted: boolean;
  isAccepted: boolean;
  showCheckout: boolean; // Controls form transition
  stripeCustomerId: string; // To store the Stripe customer ID
  isVerifyingPromoCode: boolean; // Track promo code verification status
  isPromoCodeValid: boolean; // Track if promo code is valid
  promoCodeMessage: string; // Message returned from API about the promo code
}

class SignupFormDesktop extends Component<Props, State> {
  state = {
    firstName: "",
    firstNameError: "",
    lastName: "",
    lastNameError: "",
    postcode: "",
    postcodeError: "",
    shoppingPreference: "none",
    promoCode: "",
    promoCodeError: "",
    TandC: false,
    TandCError: "",
    mailing: false,
    attemptedToSubmit: false,
    showWelcome: false,
    isLoading: false,
    isSubmitted: false,
    isAccepted: false,
    showCheckout: false, // Initially show signup form
    stripeCustomerId: "", // Will store Stripe customer ID from API
    isVerifyingPromoCode: false,
    isPromoCodeValid: false,
    promoCodeMessage: "",
  };

  // Handle going back from checkout to signup form
  handleBackToSignup = () => {
    this.setState({ showCheckout: false });

    this.props.rejectCard(false);
    this.props.updateBank(null);
    this.props.updatePromoCode(null);
  };

  submitSignupDetails = async () => {
    const isValid = this.validateForm();

    this.setState({ attemptedToSubmit: true });

    //Store here if it's the user's first time to signup
    localStorage.setItem("isFirstTimeUser", JSON.stringify(true));

    // Dispatch a custom event for tracking signup form submissions
    const auth0User = this.props.auth0.user;
    const trackingEvent = new CustomEvent("grocerize_signup_submit", {
      bubbles: true, // Allow the event to bubble up to document
      cancelable: true,
      detail: {
        source: "desktop_signup_form",
        firstName: this.state.firstName ? true : false, // Don't send actual data, just whether it exists
        lastName: this.state.lastName ? true : false,
        shoppingPreference: this.state.shoppingPreference,
        auth0_id: auth0User?.sub || null, // Include Auth0 ID for tracking
        email: auth0User?.email || null, // Include email for tracking
      },
    });
    document.dispatchEvent(trackingEvent);
    console.log("Signup submission tracked with auth0 id and email");

    if (!isValid) return;

    if (isValid) {
      const { firstName, lastName, postcode, promoCode, shoppingPreference } =
        this.state;
      const { user } = this.props.auth0;

      this.setState({ isLoading: true });

      try {
        if (user) {
          // add user data to db
          const registeredUser = await httpClient.instance.post("/user", {
            auth0_id: user.sub,
            first_name: firstName,
            last_name: lastName,
            postcode,
            promo_code: promoCode,
            shopping_preference: shoppingPreference,
            email: user.email,
          });

          console.log("REGISTEREDUSER", registeredUser);

          // Log successful registration
          console.log(
            "User successfully registered with Stripe ID:",
            registeredUser.data.stripe_id
          );

          // Send conversion data to marketing partner's webhook for user registration tracking
          try {
            const webhookUrl =
              "https://c.adsvr.io/webhooks/auth0/1ca4899b-6df2-4e7e-993b-ff7fe769e571/";
            console.log("Sending registration data to webhook:", webhookUrl);
            const webhookData = {
              auth0_id: user.sub,
              email: user.email,
              timestamp: new Date().toISOString(),
              event_type: "registration_conversion",
              stripe_customer_id: registeredUser.data.stripe_id,
            };

            const webhookResponse = await httpClient.instance.post(
              webhookUrl,
              webhookData,
              {
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
              }
            );

            console.log(
              "Marketing webhook notification sent successfully, response:",
              webhookResponse.status
            );
          } catch (webhookError: any) {
            // Log error but don't interrupt user flow
            console.error(
              "Failed to send marketing webhook notification:",
              webhookError
            );
            if (webhookError.response) {
              console.error("Error details:", webhookError.response.data);
              console.error("Error status:", webhookError.response.status);
            }
          }

          // Transition to the checkout form in the same container with a slide animation
          this.setState({
            isLoading: false,
            showCheckout: true,
            stripeCustomerId: registeredUser.data.stripe_id ?? "",
          });

          // Call the onComplete callback from parent if it exists
          if (this.props.onComplete) {
            this.props.onComplete();
          }
        }
      } catch (error) {
        console.error("Error during signup:", error);
        this.setState({ isLoading: false });
        alert("There was an error processing your signup. Please try again.");
      }
    }
  };

  validateForm = () => {
    const validation = {
      firstName: this.validateFirstName(),
      lastName: this.validateLastName(), // Now optional
      TandC: this.validateTandC(),
      // We're setting these to true since we've hidden them from the UI
      // but still need to pass validation
      postcode: true,
      promoCode: true,
    };

    console.log("Form validation result:", validation);

    return validation.firstName && validation.TandC;
  };

  validateFirstName = () => {
    if (this.state.firstName === "") {
      this.setState({
        firstNameError: "Your first name can't be blank.",
      });
      return false;
    }

    this.setState({ firstNameError: "" });
    return true;
  };

  validateLastName = () => {
    // Last name is now optional, so empty value is valid
    if (this.state.lastName === "") {
      this.setState({ lastNameError: "" });
      return true;
    }

    this.setState({ lastNameError: "" });
    return true;
  };

  validatePostCode = () => {
    // Validate postcode is not empty
    if (this.state.postcode === "") {
      this.setState({
        postcodeError: "Your postcode can't be blank.",
      });
      return false;
    }

    // Validate postcode is a number or in correct format
    const postcodeRegex = /^[0-9]{4}$/;
    if (!postcodeRegex.test(this.state.postcode)) {
      this.setState({
        postcodeError: "Your postcode should be 4 digits.",
      });
      return false;
    }

    this.setState({ postcodeError: "" });
    return true;
  };

  // Function to verify promo code with API
  verifyPromoCode = async () => {
    const { promoCode } = this.state;

    if (!promoCode) return;

    this.setState({ isVerifyingPromoCode: true });

    try {
      // Get the authentication token
      const { getAccessTokenSilently } = this.props.auth0;
      const token = await getAccessTokenSilently();

      // Call the API to verify the promo code
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/payments/verify-promo-code`,
        { promo_code: promoCode },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Process the API response
      if (response.data.valid) {
        let bankType = response.data.bankType;
        // Update state with successful validation
        this.setState({
          isPromoCodeValid: true,
          promoCodeError: "",
          promoCodeMessage:
            response.data.message || "Promo code applied successfully",
          isVerifyingPromoCode: false,
        });

        this.props.updateBank(bankType);
        this.props.updatePromoCode({
          payload: {
            description: response.data.benefits.description,
            trialDays: response.data.benefits.trialDays,
            valid: response.data.valid,
          },
        });
      } else {
        // Update state with failed validation
        this.setState({
          isPromoCodeValid: false,
          promoCodeError: response.data.message || "Invalid promo code",
          promoCodeMessage: response.data.message || "Invalid promo code",
          isVerifyingPromoCode: false,
        });

        this.props.updateBank(null);
      }
    } catch (error) {
      console.error("Error verifying promo code:", error);
      this.setState({
        isPromoCodeValid: false,
        promoCodeError: "Error verifying promo code",
        promoCodeMessage: "Error verifying promo code",
        isVerifyingPromoCode: false,
      });

      this.props.updateBank(null);
    }
  };

  // Function to clear promo code
  clearPromoCode = () => {
    this.setState({
      promoCode: "",
      promoCodeError: "",
      isVerifyingPromoCode: false,
      isPromoCodeValid: false,
      promoCodeMessage: "",
    });
    console.log("Promo code cleared");

    this.props.updateBank(null);
  };

  validatePromoCode = () => {
    // Promo code is optional, so if it's empty, it's valid
    if (this.state.promoCode === "") {
      this.setState({ promoCodeError: "" });
      return true;
    }

    // If it's not empty, it should be a valid promo code (alphanumeric)
    const promoRegex = /^[a-zA-Z0-9]+$/;
    if (!promoRegex.test(this.state.promoCode)) {
      this.setState({
        promoCodeError:
          "Your promo code should only contain letters and numbers.",
      });
      return false;
    }

    // The actual verification happens when the user clicks 'Apply'
    // but we still do basic format validation here
    return true;
  };

  validateTandC = () => {
    if (!this.state.TandC) {
      this.setState({
        TandCError:
          "To use Grocerize you need to accept the Terms and Conditions.",
      });
      return false;
    }
    this.setState({ TandCError: "" });
    return true;
  };

  getTrialPeriod = () => {
    return this.props.promoCode.trialDays ?? TRIAL_PERIOD_DAYS;
  };

  render() {
    const redirect_url =
      window.location.origin.toString() + "/sign-up-terms-of-use";
    const { showCheckout, isLoading, stripeCustomerId, promoCode } = this.state;

    return (
      <div className="u-relative premium-signup-container u-flex u-flex-column u-justify-center u-align-center u-h-all--mobile">
        <div
          className={`${
            showCheckout
              ? "u-overflow-auto u-h-max--90vh u-h-max--100vh--mobile"
              : "u-h-max--100vh--mobile"
          }`}
        >
          {/* Premium Header */}
          <div className="premium-header u-w-all">
            {showCheckout && (
              <button
                className="back-button"
                onClick={this.handleBackToSignup}
                aria-label="Go back to signup"
              >
                <span className="back-arrow">←</span> Back
              </button>
            )}
            <div className="premium-logo">
              <img
                src="/images/g-premium.svg"
                alt="Premium"
                className="premium-logo-img"
              />
            </div>
            {!showCheckout ? (
              <>
                <div className="premium-header-title">
                  Fill out your details
                </div>
                <div className="premium-header-subtitle">
                  You're almost done.
                </div>
              </>
            ) : this.props.bank.name && !this.props.card.isRejected ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "0 20px",
                }}
              >
                <div
                  style={{ flex: 1, textAlign: "left", paddingRight: "15px" }}
                >
                  <h2 className="bank-type-heading">
                    Make sure you use your {this.props.bank.name} Visa card to
                    activate your 6-month pre-paid subscription.
                  </h2>
                </div>
                <div style={{ maxWidth: "160px", flexShrink: 0 }}>
                  <img
                    src={
                      this.props.bank.name === "Newcastle Permanent"
                        ? "/images/newcastle_card_img.png"
                        : "/images/greater_bank_card_img.png"
                    }
                    alt={`${this.props.bank.name} Card`}
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "8px",
                    }}
                  />
                </div>
              </div>
            ) : (
              !this.props.card.isRejected &&
              !this.props.bank.name && (
                <>
                  <pre>{this.props.bank.name}</pre>
                  <div className="premium-header-title">
                    Maximise your savings
                  </div>
                  <div className="premium-header-title">
                    All features unlocked
                  </div>
                  <div className="premium-header-text">
                    Try it free for {this.getTrialPeriod()} days!
                  </div>
                </>
              )
            )}
          </div>

          {/* Form Container with sliding transition */}
          <div
            className={`form-container u-h-all ${
              showCheckout ? "" : "u-ph--35 u-pv--5"
            }`}
          >
            {/* Signup Form */}
            <div
              className={`form-slide signup-form ${
                showCheckout ? "slide-left" : ""
              }`}
            >
              <div className="premium-title">Your details</div>

              <div className="premium-form">
                {/* First Name */}
                <div className="premium-input-group">
                  <input
                    id="firstName"
                    className={`premium-input ${
                      this.state.firstNameError && this.state.attemptedToSubmit
                        ? "premium-input-error"
                        : ""
                    }`}
                    type="text"
                    placeholder="First Name"
                    value={this.state.firstName}
                    onChange={(e) =>
                      this.setState({ firstName: e.target.value })
                    }
                    onBlur={this.validateFirstName}
                    aria-required="true"
                    aria-label="First Name"
                  />
                  {this.state.firstNameError &&
                    this.state.attemptedToSubmit && (
                      <div className="premium-error-text">
                        {this.state.firstNameError}
                      </div>
                    )}
                </div>

                {/* Last Name (Optional) */}
                <div className="premium-input-group">
                  <input
                    id="lastName"
                    className={`premium-input ${
                      this.state.lastNameError && this.state.attemptedToSubmit
                        ? "premium-input-error"
                        : ""
                    }`}
                    type="text"
                    placeholder="Last Name (optional)"
                    value={this.state.lastName}
                    onChange={(e) =>
                      this.setState({ lastName: e.target.value })
                    }
                    onBlur={this.validateLastName}
                    aria-label="Last Name (optional)"
                  />
                  {this.state.lastNameError && this.state.attemptedToSubmit && (
                    <div className="premium-error-text">
                      {this.state.lastNameError}
                    </div>
                  )}
                </div>

                {/* Promo Code Field */}
                <div className="premium-input-group">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <input
                      id="promoCode"
                      className={`premium-input ${
                        this.state.promoCodeError &&
                        this.state.attemptedToSubmit
                          ? "premium-input-error"
                          : ""
                      }`}
                      type="text"
                      placeholder="Enter promo code"
                      value={this.state.promoCode}
                      onChange={(e) =>
                        this.setState({
                          promoCode: e.target.value,
                          isPromoCodeValid: false,
                        })
                      }
                      aria-label="Promo Code"
                      style={{ flex: 1 }}
                    />

                    {/* Show Apply or Clear buttons based on state */}
                    {this.state.promoCode &&
                      !this.state.isPromoCodeValid &&
                      !this.state.isVerifyingPromoCode && (
                        <button
                          type="button"
                          onClick={this.verifyPromoCode}
                          style={{
                            border: "none",
                            background: "#9300DA",
                            color: "white",
                            borderRadius: "4px",
                            padding: "4px 10px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            marginLeft: "8px",
                            height: "26px",
                          }}
                        >
                          Apply
                        </button>
                      )}

                    {this.state.promoCode && this.state.isPromoCodeValid && (
                      <button
                        type="button"
                        onClick={this.clearPromoCode}
                        style={{
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          padding: "0 8px",
                          fontSize: "16px",
                          color: "#666",
                        }}
                        aria-label="Clear promo code"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {/* Status messages */}
                  {this.state.isVerifyingPromoCode && (
                    <p
                      style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#666",
                        marginTop: "4px",
                        fontStyle: "italic",
                      }}
                    >
                      Verifying...
                    </p>
                  )}

                  {this.state.promoCode &&
                    !this.state.isVerifyingPromoCode &&
                    this.state.isPromoCodeValid && (
                      <p
                        style={{
                          fontSize: "12px",
                          fontWeight: 500,
                          marginTop: "4px",
                          color: "#00843d",
                        }}
                      >
                        ✓ {this.state.promoCodeMessage}
                      </p>
                    )}

                  {this.state.promoCode &&
                    !this.state.isVerifyingPromoCode &&
                    !this.state.isPromoCodeValid &&
                    this.state.promoCodeError && (
                      <p
                        style={{
                          fontSize: "12px",
                          fontWeight: 500,
                          marginTop: "4px",
                          color: "#d32f2f",
                        }}
                      >
                        {this.state.promoCodeError}
                      </p>
                    )}
                </div>

                <div className="form-divider"></div>

                {/* Shopping Preference */}
                <div className="premium-input-group">
                  <label id="shop-preference-label" className="premium-label">
                    I prefer to shop at
                  </label>
                  <div
                    className="premium-radio-group"
                    role="radiogroup"
                    aria-labelledby="shop-preference-label"
                  >
                    <div
                      className="premium-radio-item"
                      role="radio"
                      aria-checked={this.state.shoppingPreference === "coles"}
                      tabIndex={
                        this.state.shoppingPreference === "coles" ? 0 : -1
                      }
                      onClick={() =>
                        this.setState({ shoppingPreference: "coles" })
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        this.setState({ shoppingPreference: "coles" })
                      }
                    >
                      <div className="premium-radio-button">
                        {this.state.shoppingPreference === "coles" && (
                          <div className="premium-radio-selected"></div>
                        )}
                      </div>
                      <span>Coles</span>
                    </div>

                    <div
                      className="premium-radio-item"
                      role="radio"
                      aria-checked={
                        this.state.shoppingPreference === "woolworths"
                      }
                      tabIndex={
                        this.state.shoppingPreference === "woolworths" ? 0 : -1
                      }
                      onClick={() =>
                        this.setState({ shoppingPreference: "woolworths" })
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        this.setState({ shoppingPreference: "woolworths" })
                      }
                    >
                      <div className="premium-radio-button">
                        {this.state.shoppingPreference === "woolworths" && (
                          <div className="premium-radio-selected"></div>
                        )}
                      </div>
                      <span>Woolworths</span>
                    </div>

                    <div
                      className="premium-radio-item"
                      role="radio"
                      aria-checked={this.state.shoppingPreference === "none"}
                      tabIndex={
                        this.state.shoppingPreference === "none" ? 0 : -1
                      }
                      onClick={() =>
                        this.setState({ shoppingPreference: "none" })
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        this.setState({ shoppingPreference: "none" })
                      }
                    >
                      <div className="premium-radio-button">
                        {this.state.shoppingPreference === "none" && (
                          <div className="premium-radio-selected"></div>
                        )}
                      </div>
                      <span>No preference</span>
                    </div>
                  </div>
                </div>

                {/* Divider after shop preference */}
                <div className="form-divider"></div>

                {/* Terms and Conditions */}
                <div
                  className="premium-checkbox"
                  role="checkbox"
                  aria-checked={this.state.TandC}
                  tabIndex={0}
                  onClick={() =>
                    this.setState((prevState) => ({ TandC: !prevState.TandC }))
                  }
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    this.setState((prevState) => ({ TandC: !prevState.TandC }))
                  }
                >
                  <div className="premium-checkbox-box">
                    {this.state.TandC && (
                      <div className="premium-checkbox-selected"></div>
                    )}
                  </div>
                  <div className="premium-checkbox-text">
                    Yes, I have read the Grocerize{" "}
                    <a
                      className="premium-link"
                      href={redirect_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms and Conditions
                    </a>
                  </div>
                </div>
                {this.state.TandCError &&
                  (this.state.TandCError !== "" ||
                    this.state.attemptedToSubmit) && (
                    <div
                      className="premium-error-text"
                      style={{ marginTop: "-16px", marginBottom: "16px" }}
                    >
                      {this.state.TandCError}
                    </div>
                  )}

                {/* Submit Button */}
                <button
                  className="premium-button"
                  id="grocerize-signup-form-button"
                  onClick={() => {
                    // Dispatch a click event for analytics before submitting
                    const trackingClickEvent = new CustomEvent(
                      "grocerize_signup_click",
                      {
                        bubbles: true,
                        cancelable: true,
                        detail: { source: "desktop_signup_form_button" },
                      }
                    );
                    document.dispatchEvent(trackingClickEvent);

                    // Continue with normal flow
                    this.submitSignupDetails();
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? "Processing..." : "Next"}
                </button>
              </div>
            </div>

            {/* Checkout Form */}
            <div
              className={`form-slide checkout-form ${
                showCheckout ? "slide-right" : "u-hide"
              } u-h-all`}
            >
              <div className="checkout-header">
                {/* Back button moved to premium header */}
              </div>

              <div className="checkout-content">
                {showCheckout && (
                  <SingleStepCheckout
                    customerId={stripeCustomerId}
                    promoCode={promoCode}
                    hideHeaderText={false}
                    onPromoCodeClear={this.clearPromoCode}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Show loading overlay when loading */}
          {isLoading && (
            <div className="loading-overlay">
              <GrocerizeLoadingWheel />
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.user,
  card: state.card,
  bank: state.bank,
  promoCode: state.promoCode,
});

export default withAuth0<any>(
  connect(mapStateToProps, {
    showModal,
    hideModal,
    getUserData,
    checkUserStatus,
    rejectCard,
    updateBank,
    updatePromoCode,
  })(SignupFormDesktop)
);
